import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, Users, Swords, GraduationCap, Search, ExternalLink, ArrowLeft } from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import MasterDetailLayout from "@/components/layout/MasterDetailLayout";
import InlineDirectMessage from "@/components/messages/InlineDirectMessage";
import GroupChat from "@/components/training/GroupChat";
// Spår 1b: OpenMatchChat (V2) rivet — fetchOpenMatchConversations returnerar
// alltid [], så open_match_chat-typen renderar aldrig en chat-komponent.
import { useUnifiedConversations } from "@/hooks/useUnifiedConversations";
import { useMarkConversationRead } from "@/hooks/useMarkConversationRead";
import type { UnifiedConversation, ConversationType } from "@/types/conversations";

/** Smart timestamp: HH:mm today, "Yesterday" yesterday, d MMM older */
function formatConversationTime(dateStr: string, yesterday: string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, "HH:mm");
  if (isYesterday(d)) return yesterday;
  return format(d, "d MMM", { locale: sv });
}

// G-059: the "Matcher" tab (open_match_chat) is gone — V2 match chat was torn out
// in Spår 1b and fetchOpenMatchConversations always returns [], so the tab could
// never show anything. The type stays in ConversationType for back-compat.
const TABS: { key: ConversationType | "all"; labelKey: string; icon: any }[] = [
  { key: "all", labelKey: "inbox.tabs.all", icon: null },
  { key: "direct", labelKey: "inbox.tabs.direct", icon: MessageSquare },
  { key: "group_chat", labelKey: "inbox.tabs.groups", icon: Users },
  { key: "training", labelKey: "inbox.tabs.training", icon: GraduationCap },
];

const TYPE_ICONS: Record<ConversationType, any> = {
  direct: MessageSquare,
  group_chat: Users,
  match_chat: Swords,
  open_match_chat: Swords,
  training: GraduationCap,
};

export default function Messages() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const markRead = useMarkConversationRead();

  const [activeTab, setActiveTab] = useState<ConversationType | "all">("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<UnifiedConversation | null>(null);

  const filter = activeTab === "all" ? undefined : activeTab;
  const { conversations, isLoading, unreadByType } = useUnifiedConversations(filter, search || undefined);

  const handleSelect = useCallback((conv: UnifiedConversation) => {
    if (isMobile) {
      // Navigate to existing full-page routes
      switch (conv.meta.type) {
        case "direct": navigate(`/messages/${conv.meta.recipientId}`); break;
        // 8a/G-025: group chat opens INLINE here — /coach/groups/:id is
        // CoachRoute-gated, so navigating sent regular players to a wall.
        case "group_chat": setSelected(conv); break;
        case "open_match_chat": navigate(`/play/open-matches/${conv.meta.matchId}`); break;
        case "match_chat": navigate(`/play/open-matches/${conv.meta.matchId}`); break;
        case "training": navigate(`/training`); break;
      }
    } else {
      setSelected(conv);
    }
    // Mark as read
    const sourceId = conv.meta.type === "direct" ? conv.meta.recipientId
      : conv.meta.type === "group_chat" ? conv.meta.groupId
      : conv.meta.type === "match_chat" ? conv.meta.matchId
      : conv.meta.type === "open_match_chat" ? conv.meta.matchId
      : conv.meta.type === "training" ? conv.meta.groupId
      : "";
    if (conv.unreadCount > 0) markRead.mutate({ type: conv.type, sourceId });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- markRead.mutate is stable
  }, [isMobile, navigate]);

  // ─── Full-screen URL for "open in new page" ──────────
  const getFullUrl = (conv: UnifiedConversation): string => {
    switch (conv.meta.type) {
      case "direct": return `/messages/${conv.meta.recipientId}`;
      case "group_chat": return `/coach/groups/${conv.meta.groupId}`;
      case "open_match_chat": return `/play/open-matches/${conv.meta.matchId}`;
      case "match_chat": return `/play/open-matches/${conv.meta.matchId}`;
      case "training": return `/training`;
    }
  };

  // ─── Detail panel (desktop) ──────────────────────────
  const detailPanel = selected ? (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {(() => {
            const Icon = TYPE_ICONS[selected.type];
            return (
              <div
                className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: selected.color ? `${selected.color}20` : undefined }}
              >
                {selected.avatarUrl ? (
                  <img src={selected.avatarUrl} className="h-9 w-9 rounded-full object-cover" alt="" />
                ) : (
                  <Icon className="h-4 w-4 text-muted-foreground" style={selected.color ? { color: selected.color } : undefined} />
                )}
              </div>
            );
          })()}
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">
              {selected.title || (selected.type === "open_match_chat" || selected.type === "match_chat" ? t("inbox.openMatch") : t("unknown"))}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {selected.type === "group_chat" && selected.meta.type === "group_chat" && (
                <>{selected.meta.memberCount ?? 0} {t("inbox.members")}</>
              )}
              {selected.type === "open_match_chat" && t("inbox.openMatch")}
              {selected.type === "direct" && t("inbox.directMessage")}
              {selected.type === "training" && t("inbox.trainingMessage")}
            </p>
          </div>
        </div>
        {/* 8a: for group chats the full-screen target is the CoachRoute-gated
            group page — render the link only for coach/admin (G-025). */}
        {(selected.meta.type !== "group_chat" || selected.meta.isCoachOrAdmin) && (
          <button
            onClick={() => navigate(getFullUrl(selected))}
            className="shrink-0 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t("inbox.openFull")}
          </button>
        )}
      </div>

      {/* Chat content — fills remaining space */}
      <div className="flex-1 overflow-hidden">
        {selected.meta.type === "direct" && (
          <InlineDirectMessage recipientId={selected.meta.recipientId} hideHeader />
        )}
        {selected.meta.type === "group_chat" && (
          <GroupChat
            groupId={selected.meta.groupId}
            groupName={selected.meta.groupName}
            memberCount={selected.meta.memberCount}
            isCoachOrAdmin={selected.meta.isCoachOrAdmin}
          />
        )}
        {(selected.meta.type === "open_match_chat" || selected.meta.type === "match_chat") && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground p-4">
            <p className="text-sm">{t("inbox.openFull")}</p>
          </div>
        )}
        {selected.meta.type === "training" && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground p-4">
            <p className="text-sm">{t("inbox.openFull")}</p>
          </div>
        )}
      </div>
    </div>
  ) : null;

  // ─── Conversation list ───────────────────────────────
  const listPanel = (
    <div className="space-y-3 p-4 lg:p-3">
      <h1 className="text-xl font-bold lg:text-lg">{t("inbox.title")}</h1>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("inbox.search")}
          className="pl-9 h-9 text-sm"
        />
      </div>

      {/* Tabs — scrollable, hide empty types */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-1" role="tablist">
        {TABS.map(({ key, labelKey }) => {
          const isActive = activeTab === key;
          const unread = key === "all" ? unreadByType.total
            : key === "direct" ? unreadByType.direct
            : key === "group_chat" ? unreadByType.group_chat
            : key === "training" ? unreadByType.training
            : 0;
          // Hide type tabs that have 0 conversations (keep "all" always)
          if (key !== "all" && !isActive) {
            const typeConvs = conversations.filter((c) => c.type === key);
            if (typeConvs.length === 0 && unread === 0) return null;
          }
          return (
            <button
              key={key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(key)}
              className={cn(
                "shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5",
                isActive ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-muted-foreground"
              )}
            >
              {t(labelKey)}
              {unread > 0 && (
                <span className={cn(
                  "flex h-4 min-w-4 items-center justify-center rounded-full text-[9px] font-bold px-1",
                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-destructive text-destructive-foreground"
                )}>
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquare className="mx-auto h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">{search ? t("inbox.noResults") : t("inbox.noConversations")}</p>
        </div>
      ) : (
        <div className="space-y-1">
          {conversations.map((conv) => (
            <ConversationRow
              key={conv.id}
              conv={conv}
              isSelected={selected?.id === conv.id}
              onClick={() => handleSelect(conv)}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );

  // 8a/G-025: mobile inline view — MasterDetailLayout hides the detail panel on
  // mobile, so a selected group chat renders as a full-width view with a back
  // button instead of navigating to the coach-gated group page.
  if (isMobile && selected && selected.meta.type === "group_chat") {
    return (
      <div className="flex flex-col h-[calc(100vh-7rem)]">
        <div className="flex items-center gap-2 px-1 py-2 border-b shrink-0">
          <button
            onClick={() => setSelected(null)}
            aria-label={t("back")}
            className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm truncate">{selected.title || t("unknown")}</p>
            <p className="text-xs text-muted-foreground">
              {selected.meta.memberCount ?? 0} {t("inbox.members")}
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <GroupChat
            groupId={selected.meta.groupId}
            groupName={selected.meta.groupName}
            memberCount={selected.meta.memberCount}
            isCoachOrAdmin={selected.meta.isCoachOrAdmin}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 lg:pb-0 lg:h-[calc(100vh-4rem)]">
      <MasterDetailLayout
        list={listPanel}
        detail={detailPanel}
        listWidth="lg:w-96"
        emptyDetail={
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <div className="text-center space-y-2">
              <MessageSquare className="mx-auto h-10 w-10 opacity-30" />
              <p className="text-sm">{t("inbox.selectConversation")}</p>
            </div>
          </div>
        }
      />
    </div>
  );
}

// ─── Conversation row ──────────────────────────────────
function ConversationRow({ conv, isSelected, onClick, t }: {
  conv: UnifiedConversation; isSelected: boolean; onClick: () => void; t: any;
}) {
  const Icon = TYPE_ICONS[conv.type];
  const timeStr = conv.lastMessageAt ? formatConversationTime(conv.lastMessageAt, t("inbox.yesterday")) : "";

  return (
    <Card
      className={cn(
        "cursor-pointer transition-colors",
        isSelected && "border-primary bg-primary/5",
        conv.unreadCount > 0 && !isSelected && "border-primary/20"
      )}
      onClick={onClick}
    >
      <CardContent className="p-3 flex items-center gap-3">
        {/* Icon + color */}
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: conv.color ? `${conv.color}20` : undefined }}
        >
          {conv.avatarUrl ? (
            <img src={conv.avatarUrl} className="h-10 w-10 rounded-full object-cover" alt="" />
          ) : (
            <Icon className={cn("h-5 w-5", conv.color ? "" : "text-muted-foreground")} style={conv.color ? { color: conv.color } : undefined} />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={cn("text-sm truncate", conv.unreadCount > 0 ? "font-semibold" : "font-medium")}>
              {conv.title || (conv.type === "open_match_chat" || conv.type === "match_chat" ? t("inbox.openMatch") : t("unknown"))}
            </p>
            <span className="text-[10px] text-muted-foreground shrink-0">{timeStr}</span>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {conv.lastMessageIsOwn && <span className="text-foreground/60">{t("inbox.you")}: </span>}
            {conv.lastMessage}
          </p>
        </div>

        {/* Unread badge */}
        {conv.unreadCount > 0 && (
          <Badge
            className="shrink-0 h-5 min-w-5 flex items-center justify-center rounded-full text-[10px] px-1.5"
            aria-label={t("inbox.unreadCount", { count: conv.unreadCount })}
          >
            {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
